import {
  createNavigationContainerRef,
  CommonActions,
  StackActions,
} from "@react-navigation/native";
import Routes from "@navigations/Routes";
import { OrderNowLeaveGuard } from "@scenes/searchResults/orderNowLeaveGuard";

export const navigationRef = createNavigationContainerRef<any>();

export function navigate(
  name: string,
  params?: object,
  additionalParams?: object
) {
  if (navigationRef.isReady()) {
    // Use dispatch with CommonActions.navigate for cross-navigator navigation
    navigationRef.dispatch(
      CommonActions.navigate({
        name,
        params,
        ...additionalParams,
      })
    );
  } else {
    console.warn("Navigation not ready yet");
  }
}

export function navigateNested(
  navigatorName: string,
  screenName: string,
  params?: object
) {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(
      CommonActions.navigate({
        name: navigatorName,
        params: {
          screen: screenName,
          params,
        },
      })
    );
  } else {
    console.warn("Navigation not ready yet");
  }
}

export function goBack() {
  if (navigationRef.isReady() && navigationRef.canGoBack()) {
    navigationRef.goBack();
  }
}

export function reset(routeName: string, params?: object) {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: routeName, params }],
      })
    );
  }
}

export function push(name: string, params?: object) {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(StackActions.push(name, params));
  }
}

export function replace(name: string, params?: object) {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(StackActions.replace(name, params));
  }
}

const withoutOrderNowScreen = (
  navState: any,
  navigatorName: string,
  screenName: string
): { state: any; changed: boolean } => {
  if (!navState?.routes) return { state: navState, changed: false };

  let changed = false;
  const routes = navState.routes.map((route: any) => {
    if (route.name === navigatorName && route.state?.routes) {
      const stackRoutes = route.state.routes;
      if (!stackRoutes.some((entry: any) => entry.name === screenName)) {
        return route;
      }
      const nextRoutes = stackRoutes.filter(
        (entry: any) => entry.name !== screenName
      );
      if (!nextRoutes.length) return route;
      changed = true;
      const currentIndex = route.state.index ?? stackRoutes.length - 1;
      const current = stackRoutes[currentIndex];
      let nextIndex = nextRoutes.findIndex(
        (entry: any) => entry.key === current?.key
      );
      if (nextIndex === -1) {
        const previous = [...stackRoutes.slice(0, currentIndex)]
          .reverse()
          .find((entry: any) => entry.name !== screenName);
        nextIndex = previous
          ? nextRoutes.findIndex((entry: any) => entry.key === previous.key)
          : 0;
        if (nextIndex < 0) nextIndex = 0;
      }
      return {
        ...route,
        state: {
          ...route.state,
          routes: nextRoutes,
          index: nextIndex,
        },
      };
    }

    if (route.state) {
      const nested = withoutOrderNowScreen(
        route.state,
        navigatorName,
        screenName
      );
      if (nested.changed) {
        changed = true;
        return { ...route, state: nested.state };
      }
    }
    return route;
  });

  return {
    state: changed ? { ...navState, routes } : navState,
    changed,
  };
};

export function removeOrderNowScreenKeepingFocus() {
  if (!navigationRef.isReady()) return;

  const current = navigationRef.getRootState();
  const { state, changed } = withoutOrderNowScreen(
    current,
    Routes.ExploreNavigator,
    Routes.OrderNowJewelryVariationScreen
  );
  if (!changed) return;

  OrderNowLeaveGuard.allowNextRemoval();
  navigationRef.dispatch(
    CommonActions.reset({
      ...state,
      index: current.index ?? state.index,
    })
  );
}

export function withoutBackgroundOrderNow(navState: any) {
  if (!navState?.routes) return navState;
  const focusedName = getFocusedRouteName(navState);
  if (focusedName === Routes.OrderNowJewelryVariationScreen) {
    return navState;
  }
  return withoutOrderNowScreen(
    navState,
    Routes.ExploreNavigator,
    Routes.OrderNowJewelryVariationScreen
  ).state;
}

const getFocusedRouteName = (navState: any): string | undefined => {
  if (!navState?.routes?.length) return undefined;
  const route = navState.routes[navState.index ?? 0];
  if (!route) return undefined;
  if (route.state) {
    return getFocusedRouteName(route.state) ?? route.name;
  }
  return route.name;
};
